import { useEffect, useRef, useContext } from 'react'

import { OptimizelyContext } from '@optimizely/react-sdk'
import Avatar from '@space-core/components/customAvatar'
import CustomButton from '@space-core/components/customButton'
import { VariationTypes } from '@space-core/components/customButton/types'
import Icon from '@space-core/components/icon'

import { Actions, Entities } from '@space-core/datahub'
import clsx from 'clsx'
import styled from 'styled-components'

import type { DropdownUserProps } from '../types'

const CustomIcon = styled(Icon)`
  height: 1.25rem;
`

const ContainerUser = styled.div`
  cursor: default;
`

const BorderAvatar = styled.div<{ avatarHighlight?: boolean }>`
  border: ${(props) => props.avatarHighlight && `0.125rem solid var(--green)`};
  border-radius: 50%;

  div {
    border: ${(props) => props.avatarHighlight && `0.125rem solid var(--white)`};
    border-radius: 50%;
  }
`

const ProfileButton = styled(CustomButton)`
  border-radius: var(--border-radius-sm);
  margin-top: var(--spacer-2);
  color: var(--white);
  margin-bottom: var(--spacer-2);

  &:hover {
    background-color: var(--gray-200);
    outline: 0;
    text-decoration: none;
  }
`
const AvatarTooltip = styled.span`
  height: 2.19rem;
  right: 4.38rem;
  z-index: 2;

  &::before {
    content: '';
    border-left: 0.438rem solid var(--gray-900);
    border-top: 0.438rem solid transparent;
    border-right: 0.438rem solid transparent;
    border-bottom: 0.438rem solid transparent;
    position: absolute;
    transform: translateY(-50%);
    top: 50%;
    left: 100%;
  }
`

const DropdownUser = ({
  children,
  dropdownItems,
  email,
  icon,
  initialLetters,
  userName,
  urlAvatar,
  sendDataHubEvent,
  customColor,
  onClickProfile,
  avatarTooltipText,
  avatarHighlight,
  isLogged,
  theme,
  onDisableAvatarHighlight,
  profileButtonLabel
}: DropdownUserProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const itemRef = useRef<HTMLButtonElement>(null)
  const { optimizely } = useContext(OptimizelyContext)

  useEffect(() => {
    if (dropdownRef.current) {
      const shadowRootDropdown = dropdownRef.current.shadowRoot
      if (shadowRootDropdown) {
        const dropdownElement = shadowRootDropdown.querySelector(
          '.hot-dropdown__menu'
        ) as HTMLDivElement
        if (dropdownElement) {
          dropdownElement.style.borderColor = 'var(--gray-200)'
        }
      }
    }
  }, [])

  const handleClickDropdownUser = () => {
    onDisableAvatarHighlight?.()
  }

  const handleClickAvatar = () => {
    optimizely?.track('click_avatar')
    sendDataHubEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_PROFILE
    })
  }

  const stopEventClick = async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation()
  }

  const avatarProps = userName
    ? {
        initialLetters: initialLetters,
        urlAvatar: urlAvatar,
        customColor: customColor,
        isLogged: isLogged
      }
    : {
        icon: icon,
        isLogged: isLogged,
        theme: theme
      }

  return (
    <hot-dropdown
      onClick={stopEventClick}
      ref={dropdownRef}
      slot='actions-end'
      data-test='structure-header-dropdown'>
      <hot-menu-item
        ref={itemRef}
        slot='button'
        class='hot-application--minimized _cursor-pointer'
        data-test='dropdown-button-start'>
        {avatarTooltipText && (
          <AvatarTooltip
            data-test='tooltip-element'
            className='_bg-gray-900 _text-gray-100 _d-inline-flex _position-fixed _justify-content-center _align-items-center _px-4 _rounded _text-1 _font-weight-bold'>
            {avatarTooltipText}
          </AvatarTooltip>
        )}
        <div className='hot-application-menu__icon'>
          {avatarHighlight ? (
            <BorderAvatar data-test='avatar-highlight' avatarHighlight={avatarHighlight}>
              <div>
                <Avatar
                  className='hot-avatar--sm'
                  data-test='avatar-button'
                  onClick={handleClickAvatar}
                  {...avatarProps}
                />
              </div>
            </BorderAvatar>
          ) : (
            <Avatar
              data-test='avatar-button'
              className='hot-avatar--sm'
              onClick={handleClickAvatar}
              {...avatarProps}
            />
          )}
        </div>
      </hot-menu-item>

      {userName && (
        <hot-dropdown-menu-header
          slot='menu'
          class='_mt-5 _ml-5 _p-0'
          onClick={handleClickDropdownUser}
          data-test='dropdown-info-user'>
          <ContainerUser data-test='dropdown-info-user-content' className='_d-flex _mr-5 _py-1'>
            <BorderAvatar avatarHighlight={avatarHighlight} className='_align-self-baseline'>
              <div>
                <Avatar
                  className='hot-avatar--xl _text-gray-700'
                  initialLetters={initialLetters}
                  urlAvatar={urlAvatar}
                  customColor={customColor}
                />
              </div>
            </BorderAvatar>
            <div className='_ml-4 _d-flex _flex-column _justify-content-center'>
              <p className='_text-2 _text-gray-900 _mb-0'>{userName}</p>
              <p className='_text-1 _text-gray-500 _mb-0'>{email}</p>
              {profileButtonLabel && (
                <ProfileButton
                  data-test='button-click-profile'
                  variation={VariationTypes.SECONDARY}
                  size='sm'
                  onClick={onClickProfile}>
                  {profileButtonLabel}
                </ProfileButton>
              )}
            </div>
          </ContainerUser>
        </hot-dropdown-menu-header>
      )}
      <div
        slot='menu'
        className={clsx('_mx-5  _p-0', {
          '_mt-3': userName,
          '_mt-5': !userName
        })}
        data-test='dropdown-switch-theme'>
        {children}
      </div>
      <hot-dropdown-menu-divider slot='menu' class='_mx-5 _my-0' />
      {dropdownItems?.map(({ label, handleClick, iconCustom, variation }, index) => (
        <hot-dropdown-menu-item
          key={index}
          slot='menu'
          onClick={handleClick}
          data-test='dropdown-items'
          class={clsx('_mx-5', {
            '_text-gray-900 _my-4': iconCustom,
            '_my-2': !iconCustom
          })}>
          {iconCustom ? (
            <>
              <CustomIcon icon={iconCustom} className='_mr-3' /> {label}
            </>
          ) : (
            <CustomButton
              size='block'
              onClick={handleClick}
              variation={variation}
              data-test='dropdown-item-button'>
              {label}
            </CustomButton>
          )}
        </hot-dropdown-menu-item>
      ))}
    </hot-dropdown>
  )
}

export default DropdownUser
